import { Suspense, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ToolsLinkStrip } from "@/components/marketing/ToolsLinkStrip";
import { TabbedHeroSection } from "@/components/home/TabbedHeroSection";
import { usePersona } from "@/contexts/PersonaContext";
import {
  SocialProofBarLazy as SocialProofBar,
  WhyYoutempSectionLazy as WhyYoutempSection,
  WhoWeServeSectionLazy as WhoWeServeSection,
  CoreValueSectionLazy as CoreValueSection,
  FeaturesGridSectionLazy as FeaturesGridSection,
  DifferentiatorsSectionLazy as DifferentiatorsSection,
  StatsHighlightSectionLazy as StatsHighlightSection,
  FAQSectionLazy as FAQSection,
  NewCTASectionLazy as NewCTASection,
  FromOurBlogStripLazy as FromOurBlogStrip,
} from "@/lib/prerenderedHomeChunks";

// Phase 4 — IntersectionObserver gate so the blog strip chunk is fetched
// only as the user nears it. The wrapping `cv-home-medium` placeholder
// keeps reserved height to avoid CLS.
function BlogStripIntersectionGate() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [visible]);

  return (
    <div ref={ref} className="cv-home-medium">
      {visible && (
        <Suspense fallback={null}>
          <FromOurBlogStrip heading="Latest from the blog" />
        </Suspense>
      )}
    </div>
  );
}

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "Youtemp",
      "url": "https://youtemp.co.uk",
      "logo": "https://youtemp.co.uk/youtemp-logo.png",
      "description": "UK temporary staffing marketplace where staffing agencies, employers and temporary workers manage shifts, fill vacancies and cut admin.",
      "areaServed": { "@type": "Country", "name": "United Kingdom" },
    },
    {
      "@type": "WebSite",
      "name": "Youtemp",
      "url": "https://youtemp.co.uk",
    },
  ],
};

export default function Home() {
  const { activeAudience } = usePersona();

  return (
    <>
      <Helmet>
        <title>UK Temporary Staffing for Agencies, Employers & Workers</title>
        <meta name="description" content="Youtemp is a UK temporary staffing platform for agencies, employers and workers - fill shifts, find flexible work and manage your workforce in one place." />
        <link rel="canonical" href="https://youtemp.co.uk" />
        <meta property="og:title" content="UK Temporary Staffing for Agencies, Employers & Workers" />
        <meta property="og:description" content="Youtemp is a UK temporary staffing platform for agencies, employers and workers - fill shifts, find flexible work and manage your workforce in one place." />
        <meta property="og:url" content="https://youtemp.co.uk" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://youtemp.co.uk/og/og-home.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="UK Temporary Staffing for Agencies, Employers & Workers" />
        <meta name="twitter:description" content="UK temporary staffing marketplace for staffing agencies, employers and temporary workers to manage shifts and fill vacancies." />
        <meta name="twitter:image" content="https://youtemp.co.uk/og/og-home.jpg" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <TabbedHeroSection activeAudience={activeAudience} />

      {/* Below-fold sections render eagerly so prerendered HTML matches the
          hydrated DOM (the previous `useDeferredRender` gate unmounted them
          on hydration and remounted after idle, causing a visible pop on
          hard refresh). Vite still code-splits these via `lazy()`.
          Each section is wrapped in `cv-auto-section` so the browser can
          skip layout+paint for off-screen sections, eliminating the
          mass-mount forced reflow that PSI attributed to home-sections.js. */}
      <div className="cv-home-short">
        <Suspense fallback={null}>
          <SocialProofBar activeAudience={activeAudience} />
        </Suspense>
      </div>

      {/* Each section gets its own Suspense boundary so React can commit
          them independently as their chunks resolve. Far-below-fold
          sections (Stats, FAQ, Blog, CTA) are deferred via
          `prerenderedHomeChunks` and warmed during browser idle. */}
      <div className="cv-home-medium"><Suspense fallback={null}><WhyYoutempSection activeAudience={activeAudience} /></Suspense></div>
      <div className="cv-home-medium"><Suspense fallback={null}><WhoWeServeSection activeAudience={activeAudience} /></Suspense></div>
      <div className="cv-home-tall"><Suspense fallback={null}><CoreValueSection activeAudience={activeAudience} /></Suspense></div>
      <div className="cv-home-tall"><Suspense fallback={null}><FeaturesGridSection activeAudience={activeAudience} /></Suspense></div>
      <div className="cv-home-tall"><Suspense fallback={null}><DifferentiatorsSection activeAudience={activeAudience} /></Suspense></div>
      <div className="cv-home-medium"><Suspense fallback={null}><StatsHighlightSection activeAudience={activeAudience} /></Suspense></div>
      <div className="cv-home-tall"><Suspense fallback={null}><FAQSection activeAudience={activeAudience} /></Suspense></div>
      <BlogStripIntersectionGate />
      <ToolsLinkStrip />
      <div className="cv-home-medium"><Suspense fallback={null}><NewCTASection activeAudience={activeAudience} /></Suspense></div>
    </>
  );
}
